"use client"

import { useState, useEffect, useRef } from "react"
import { ChevronLeft, ChevronRight, Shield } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export function PrivacyToggle() {
  const [isHovered, setIsHovered] = useState(false)
  const [ready, setReady] = useState(false)
  const preferencesLinkRef = useRef<HTMLAnchorElement | null>(null)

  useEffect(() => {
    // Hide the default Iubenda floating button completely
    const hideDefaultButton = () => {
      const btn = document.querySelector('.iubenda-uspr-btn') as HTMLElement | null
      if (btn) {
        btn.style.display = 'none !important'
      }
    }

    hideDefaultButton()

    // Find the real preferences link (opens full privacy choices)
    const findPreferencesLink = () => {
      const link = document.querySelector('.iubenda-cs-preferences-link') as HTMLAnchorElement | null
      if (link) {
        preferencesLinkRef.current = link
        setReady(true)
      } else {
        // Retry if Iubenda loads async
        setTimeout(findPreferencesLink, 300)
      }
    }

    findPreferencesLink()

    // Fallback: if no link found after some time, we can log or use alternative
  }, [])

  const openPrivacy = () => {
    if (preferencesLinkRef.current) {
      // Simulate click on real Iubenda link → opens the modal reliably
      preferencesLinkRef.current.click()
    }
  }

  return (
    <div
      className="fixed bottom-6 left-6 z-[9999] pointer-events-auto"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Button
        onClick={openPrivacy}
        size="icon"
        variant="outline"
        disabled={!ready}
        className={cn(
          "group relative h-12 w-12 rounded-full transition-all duration-300 ease-out overflow-hidden",
          "bg-gradient-to-br from-white/95 via-white/80 to-white/60",
          "backdrop-blur-lg border border-primary/30 shadow-lg",
          "hover:shadow-[0_16px_48px_-10px_rgba(59,130,246,0.35)]",
          "hover:scale-110 active:scale-95",
          !ready && "opacity-60 cursor-not-allowed"
        )}
      >
        {/* Glide / shine overlay */}
        <div
          className={cn(
            "absolute inset-0 rounded-full pointer-events-none z-20",
            "bg-gradient-to-r from-transparent via-white/80 to-transparent",
            "w-[200%] h-full -translate-x-full",
            "group-hover:translate-x-0 transition-transform duration-700 ease-out"
          )}
        />

        {/* Icons with smooth animation – no overlap */}
        <div className="relative flex items-center justify-center w-8 h-8 z-10">
          <Shield
            className={cn(
              "h-8 w-8 text-primary transition-all duration-350",
              isHovered ? "scale-115 rotate-12" : "scale-100 rotate-0"
            )}
            strokeWidth={2}
          />
        </div>
      </Button>

      {/* Nice hover tooltip */}
      <div
        className={cn(
          "absolute bottom-full left-[50px] -translate-x-1/2 mb-3 px-4 py-2",
          "bg-black/80 backdrop-blur-sm text-white text-sm rounded-lg whitespace-nowrap shadow-md",
          "transition-all duration-300 pointer-events-none",
          isHovered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
        )}
      >
        Privacy Settings
      </div>
    </div>
  )
}